/**
 * AdminTravelToolsWidgets — self-contained admin panel for the Travel Tools sidebar widgets.
 *
 * Controls four widgets that live on the user-facing Travel Tools page:
 *   - Prayer Times (cities list, API URL, calculation method, titles)
 *   - MAD Converter (currencies, fallback rates, API URL, titles, disclaimer)
 *   - Daily Goal (default goal, min/max range, titles)
 *   - Weekly Progress (on/off + titles)
 *
 * Why a separate file: keeps each new feature in its own module per project preference
 * ("Keep modules separated — new features get their own files").
 *
 * Wiring: AdminTravelTools.js imports this component and renders it under activeTab === 'widgets'.
 * Backend endpoints used:
 *   - GET  /api/travel-tools/admin/widgets-config
 *   - PUT  /api/travel-tools/admin/widgets-config
 *   - POST /api/travel-tools/admin/widgets-config/reset
 */
import React, { useEffect, useState } from 'react';

const SectionCard = ({ icon, title, hint, children }) => (
  <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
    <div className="px-5 py-4 bg-gradient-to-r from-gray-800 to-gray-700 border-b border-gray-700">
      <div className="flex items-center gap-3">
        <span className="text-3xl">{icon}</span>
        <div>
          <h3 className="text-lg font-bold text-white">{title}</h3>
          {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
        </div>
      </div>
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const Toggle = ({ on, onClick, onLabel = 'Enabled', offLabel = 'Disabled' }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
      on ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
    }`}
  >
    {on ? `✓ ${onLabel}` : `✕ ${offLabel}`}
  </button>
);

const TextInput = (props) => (
  <input
    type="text"
    {...props}
    className={`w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${props.className || ''}`}
  />
);

const NumberInput = (props) => (
  <input
    type="number"
    {...props}
    className={`w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${props.className || ''}`}
  />
);

const SaveButton = ({ onClick, saving, label = 'Save' }) => (
  <button
    onClick={onClick}
    disabled={saving}
    className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold transition-colors"
  >
    {saving ? 'Saving…' : `💾 ${label}`}
  </button>
);

const AdminTravelToolsWidgets = ({ API, authHeaders, onMessage }) => {
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  // Fallback-rates draft must live as a hook at the top level of the component (Rules of Hooks).
  // We initialize empty and sync it from `config` once it loads.
  const [fallbackRatesDraft, setFallbackRatesDraft] = useState('');
  // Prayer cities draft — kept as raw text so the user can press Enter to add a new line.
  // If we derived the textarea value from the parsed array on every keystroke, `filter(Boolean)`
  // would strip empty lines immediately and the cursor would jump back, preventing new entries.
  const [citiesDraft, setCitiesDraft] = useState('');
  // Converter API URLs draft — same pattern: raw text for typing, parse on save.
  const [apiUrlsDraft, setApiUrlsDraft] = useState('');

  const toast = (msg) => {
    if (typeof onMessage === 'function') onMessage(msg);
  };

  useEffect(() => {
    let aborted = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/travel-tools/admin/widgets-config`, { headers: authHeaders });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!aborted) {
          setConfig(data);
          setFallbackRatesDraft(JSON.stringify(data?.converter?.fallback_rates_per_mad || {}, null, 2));
          setCitiesDraft((data?.prayer?.cities || []).join('\n'));
          // Backwards-compat: if doc still has old `api_url`, seed draft from it; otherwise use api_urls list.
          const seededUrls = Array.isArray(data?.converter?.api_urls) && data.converter.api_urls.length > 0
            ? data.converter.api_urls
            : (data?.converter?.api_url ? [data.converter.api_url] : []);
          setApiUrlsDraft(seededUrls.join('\n'));
        }
      } catch (e) {
        if (!aborted) setError(String(e.message || e));
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [API, authHeaders]);

  const patch = (key, partial) => {
    setConfig((prev) => ({ ...prev, [key]: { ...(prev?.[key] || {}), ...partial } }));
  };

  const saveSection = async (key, overrideValue) => {
    setSavingKey(key);
    try {
      // Accept an explicit value override so we don't depend on stale `config` closure
      // when callers also schedule a state update right before saving (e.g. cities draft commit).
      const value = overrideValue !== undefined ? overrideValue : config[key];
      const body = { [key]: value };
      const res = await fetch(`${API}/api/travel-tools/admin/widgets-config`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.config) setConfig(data.config);
      toast(`${key} saved`);
    } catch (e) {
      toast(`Save failed: ${e.message || e}`);
    } finally {
      setSavingKey(null);
    }
  };

  const resetAll = async () => {
    if (!window.confirm('Reset ALL widget settings to factory defaults? This cannot be undone.')) return;
    setSavingKey('reset');
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/widgets-config/reset`, {
        method: 'POST',
        headers: authHeaders
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.config) setConfig(data.config);
      toast('Widgets config reset to defaults');
    } catch (e) {
      toast(`Reset failed: ${e.message || e}`);
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="bg-gray-800 rounded-xl p-12 text-center text-gray-400">Loading widgets config…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-700 rounded-xl p-6 text-red-200">
        <div className="font-bold mb-2">Could not load widgets config</div>
        <div className="text-sm">{error}</div>
        <div className="text-xs text-red-300 mt-2">If this is the first time using this tab, make sure the backend has been restarted to pick up the new endpoints.</div>
      </div>
    );
  }
  if (!config) return null;

  const prayer = config.prayer || {};
  const converter = config.converter || {};
  const dailyGoal = config.daily_goal || {};
  const weekly = config.weekly_progress || {};

  // Prayer cities: textarea is wired to `citiesDraft` raw text so newlines / empty rows survive
  // while typing. We only parse + filter when the user clicks Save (commitCitiesDraft).
  const commitCitiesDraft = () => {
    const cities = citiesDraft.split('\n').map(s => s.trim()).filter(Boolean);
    patch('prayer', { cities });
    return cities;
  };

  // Converter API URLs: same pattern as cities — raw text while typing, parse on save.
  const commitApiUrlsDraft = () => {
    const urls = apiUrlsDraft.split('\n').map(s => s.trim()).filter(Boolean);
    patch('converter', { api_urls: urls });
    return urls;
  };

  // Fallback rates edited as JSON for transparency. Validated on save (commit).
  // Returns the parsed object (or null on failure) so the caller can pass it directly to saveSection,
  // avoiding stale-closure issues from the pending setState.
  const commitFallbackRates = () => {
    try {
      const parsed = JSON.parse(fallbackRatesDraft);
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) throw new Error('Must be a JSON object');
      patch('converter', { fallback_rates_per_mad: parsed });
      return parsed;
    } catch (e) {
      toast(`Invalid fallback rates JSON: ${e.message}`);
      return null;
    }
  };

  // Converter currencies edited comma-separated
  const currenciesText = (converter.currencies || []).join(', ');
  const setCurrenciesText = (text) => {
    const list = text.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    patch('converter', { currencies: list });
  };

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="bg-gradient-to-r from-pink-900/40 to-purple-900/40 rounded-xl p-6 border border-pink-700/50">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-4xl">🧩</span>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white">Widgets</h2>
            <p className="text-gray-300 text-sm">
              Control the four sidebar widgets on the user-facing Travel Tools page. Toggle each on/off,
              override API URLs (kill switch if an external service breaks), and edit titles, defaults, and limits.
            </p>
          </div>
          <button
            onClick={resetAll}
            disabled={savingKey === 'reset'}
            className="px-3 py-2 rounded-lg bg-amber-700/60 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-bold transition-colors whitespace-nowrap"
            title="Restore all widget settings to factory defaults"
          >
            ↺ Reset all
          </button>
        </div>
      </div>

      {/* 🕌 Prayer Times */}
      <SectionCard icon="🕌" title="Prayer Times" hint="Shows today's 5 prayers + city selector at the top of the sidebar.">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-300">Widget visibility</span>
          <Toggle on={!!prayer.enabled} onClick={() => patch('prayer', { enabled: !prayer.enabled })} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title" hint="Heading shown on the widget">
            <TextInput value={prayer.title || ''} onChange={(e) => patch('prayer', { title: e.target.value })} />
          </Field>
          <Field label="Subtitle" hint="Small text under the title">
            <TextInput value={prayer.subtitle || ''} onChange={(e) => patch('prayer', { subtitle: e.target.value })} />
          </Field>
        </div>
        {/* 5 Prayer Names — editable display labels. The aladhan API still uses the canonical
            keys (Fajr/Dhuhr/Asr/Maghrib/Isha) for the time data; these inputs only override
            what the USER sees on the widget. Leave blank to use the default name. */}
        <Field label="Prayer names (display only)" hint="Customize how each prayer name appears on the widget. The data still comes from the API using canonical names — this only changes the label users see.">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {[
              { key: 'Fajr',    emoji: '🌅' },
              { key: 'Dhuhr',   emoji: '☀️' },
              { key: 'Asr',     emoji: '🌤️' },
              { key: 'Maghrib', emoji: '🌇' },
              { key: 'Isha',    emoji: '🌙' },
            ].map(p => {
              const names = prayer.names || {};
              return (
                <div key={p.key}>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">
                    {p.emoji} {p.key}
                  </div>
                  <TextInput
                    value={names[p.key] ?? ''}
                    placeholder={p.key}
                    onChange={(e) => patch('prayer', { names: { ...names, [p.key]: e.target.value } })}
                  />
                </div>
              );
            })}
          </div>
        </Field>
        <Field label="API URL" hint="aladhan.com base URL — change if the public service moves or you self-host">
          <TextInput value={prayer.api_url || ''} onChange={(e) => patch('prayer', { api_url: e.target.value })} />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Calculation method" hint="21 = Morocco Habous (official). Other countries use different methods.">
            <NumberInput value={prayer.method ?? 21} onChange={(e) => patch('prayer', { method: parseInt(e.target.value, 10) || 21 })} />
          </Field>
          <Field label="Default city" hint="Auto-selected on first visit">
            <TextInput value={prayer.default_city || ''} onChange={(e) => patch('prayer', { default_city: e.target.value })} />
          </Field>
        </div>
        <Field label="Cities (one per line)" hint="Add any Moroccan city — the prayer API accepts any city name. Press Enter to add a new row. Empty lines are removed when you Save.">
          <textarea
            value={citiesDraft}
            onChange={(e) => setCitiesDraft(e.target.value)}
            rows={10}
            className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder={`Marrakech\nCasablanca\nRabat\nFez\nSafi`}
          />
        </Field>
        <SaveButton
          onClick={() => {
            const cities = commitCitiesDraft();
            saveSection('prayer', { ...prayer, cities });
          }}
          saving={savingKey === 'prayer'}
          label="Save Prayer settings"
        />
      </SectionCard>

      {/* 💱 MAD Converter */}
      <SectionCard icon="💱" title="MAD Converter" hint="Foreign-currency to MAD quick converter with daily-refreshed live rates.">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-300">Widget visibility</span>
          <Toggle on={!!converter.enabled} onClick={() => patch('converter', { enabled: !converter.enabled })} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title">
            <TextInput value={converter.title || ''} onChange={(e) => patch('converter', { title: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <TextInput value={converter.subtitle || ''} onChange={(e) => patch('converter', { subtitle: e.target.value })} />
          </Field>
        </div>
        <Field label="API URLs (one per line, tried in order)" hint="Frontend tries each URL top-to-bottom. First one to return valid rates wins. If all fail, the fallback rates below are used. Placeholders: {BASE} = uppercase (MAD), {base} = lowercase (mad), {SYMBOLS} = comma-separated currencies.">
          <textarea
            value={apiUrlsDraft}
            onChange={(e) => setApiUrlsDraft(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            placeholder={`https://open.er-api.com/v6/latest/{BASE}\nhttps://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/{base}.json\nhttps://latest.currency-api.pages.dev/v1/currencies/{base}.json`}
          />
        </Field>
        <Field label="Currencies (comma-separated)" hint="Currencies shown in the dropdown. Frontend always converts TO MAD.">
          <TextInput value={currenciesText} onChange={(e) => setCurrenciesText(e.target.value)} placeholder="EUR, USD, GBP" />
        </Field>
        <Field label="Fallback rates (foreign per 1 MAD)" hint="Used when the live API fails. JSON object. Example: 1 EUR = 0.092 MAD-equivalent, meaning 1 EUR ≈ 10.87 MAD.">
          <textarea
            value={fallbackRatesDraft}
            onChange={(e) => setFallbackRatesDraft(e.target.value)}
            onBlur={commitFallbackRates}
            rows={5}
            className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          />
        </Field>
        <Field label="Disclaimer text" hint="Small print shown under the converted amount">
          <TextInput value={converter.disclaimer || ''} onChange={(e) => patch('converter', { disclaimer: e.target.value })} />
        </Field>
        <SaveButton
          onClick={() => {
            const parsedRates = commitFallbackRates();
            // If parsing failed, abort save (toast already shown)
            if (parsedRates === null) return;
            const urls = commitApiUrlsDraft();
            saveSection('converter', { ...converter, api_urls: urls, fallback_rates_per_mad: parsedRates });
          }}
          saving={savingKey === 'converter'}
          label="Save Converter settings"
        />
      </SectionCard>

      {/* 🎯 Daily Goal */}
      <SectionCard icon="🎯" title="Daily Goal" hint="Progress ring showing today's phrases-added vs target.">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-300">Widget visibility</span>
          <Toggle on={!!dailyGoal.enabled} onClick={() => patch('daily_goal', { enabled: !dailyGoal.enabled })} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title">
            <TextInput value={dailyGoal.title || ''} onChange={(e) => patch('daily_goal', { title: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <TextInput value={dailyGoal.subtitle || ''} onChange={(e) => patch('daily_goal', { subtitle: e.target.value })} />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Default goal" hint="Used the first time a user opens the page">
            <NumberInput value={dailyGoal.default_goal ?? 3} onChange={(e) => patch('daily_goal', { default_goal: parseInt(e.target.value, 10) || 3 })} min={1} />
          </Field>
          <Field label="Min goal" hint="Lower bound of ± buttons">
            <NumberInput value={dailyGoal.min_goal ?? 1} onChange={(e) => patch('daily_goal', { min_goal: parseInt(e.target.value, 10) || 1 })} min={1} />
          </Field>
          <Field label="Max goal" hint="Upper bound of ± buttons">
            <NumberInput value={dailyGoal.max_goal ?? 20} onChange={(e) => patch('daily_goal', { max_goal: parseInt(e.target.value, 10) || 20 })} min={1} />
          </Field>
        </div>
        <SaveButton onClick={() => saveSection('daily_goal')} saving={savingKey === 'daily_goal'} label="Save Daily Goal settings" />
      </SectionCard>

      {/* 📊 Weekly Progress */}
      <SectionCard icon="📊" title="Weekly Progress" hint="Bar chart of phrases added across the last 7 days.">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-300">Widget visibility</span>
          <Toggle on={!!weekly.enabled} onClick={() => patch('weekly_progress', { enabled: !weekly.enabled })} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title">
            <TextInput value={weekly.title || ''} onChange={(e) => patch('weekly_progress', { title: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <TextInput value={weekly.subtitle || ''} onChange={(e) => patch('weekly_progress', { subtitle: e.target.value })} />
          </Field>
        </div>
        <SaveButton onClick={() => saveSection('weekly_progress')} saving={savingKey === 'weekly_progress'} label="Save Weekly Progress settings" />
      </SectionCard>
    </div>
  );
};

export default AdminTravelToolsWidgets;
